import { RegionHealthCheckResponseSchema } from "@amzn/client-workbench-api-model";
import { Auth, CognitoUser } from "@aws-amplify/auth";
import { Amplify } from "@aws-amplify/core";
import { trackStructEvent } from "@snowplow/browser-tracker";
import axios, { AxiosResponse } from "axios";
import { useContext } from "react";
import { useQuery, UseQueryResult } from "react-query";

import { EmbeddedMetric, Logger } from "@/apis/Logger";
import { AUTH_PARAM, IDPRISM_AUTH_VALUE } from "@/helpers/landing";
import { getRetryDelayMs, shouldRetry } from "@/helpers/react-query-helper";
import { SnowplowTracker } from "@/helpers/snowplow";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";
import { Day1EventAction, Day1EventCategory } from "@/types/snowplow-events";

import { getAmplifyConfig, getStageConfig } from "../amplifyConfig";
import { ActivatedRegions, DEFAULT_REGION } from "../amplifyStageConfig";

/**
 * The PPv2 login hint appended to the Cognito authorize URL by default. Routing
 * is driven by the `auth` query param (see {@link AUTH_PARAM}): `ppv2` (the
 * default when the param is absent or any other value) keeps this hint;
 * `idprism` (see {@link IDPRISM_AUTH_VALUE}) omits it so Cognito routes to
 * IDPrism instead.
 */
const PPV2_LOGIN_HINT = "&login_hint=ppv2";

/**
 * Emit a remote logging message when an API active region transition occurs
 * e.g. us-west-2 --> us-east-1 is a transition to a failover region
 */
const logActiveRegionTransition = (
  currentActiveRegion: ActivatedRegions | undefined,
  nextActiveRegion: ActivatedRegions
): void => {
  const isFailingOver =
    currentActiveRegion &&
    currentActiveRegion !== nextActiveRegion &&
    nextActiveRegion !== DEFAULT_REGION;
  const isRecoveringBack =
    currentActiveRegion &&
    currentActiveRegion !== nextActiveRegion &&
    nextActiveRegion === DEFAULT_REGION;
  const isInitializingToDefault =
    !currentActiveRegion && nextActiveRegion === DEFAULT_REGION;
  const isInitializingToFailover =
    !currentActiveRegion && nextActiveRegion !== DEFAULT_REGION;
  if (isFailingOver) {
    void Logger.warn(
      `Failing over API active region from ${currentActiveRegion} to ${nextActiveRegion}`
    );
  } else if (isRecoveringBack) {
    void Logger.info(
      `Default API active region is up, recovering back from ${currentActiveRegion} to ${nextActiveRegion}`
    );
  } else if (isInitializingToDefault) {
    void Logger.info(`Initializing API active region to: ${nextActiveRegion}`);
  } else if (isInitializingToFailover) {
    void Logger.warn(`Initializing API active region to: ${nextActiveRegion}`);
  }
};

export interface AmplifyInitProps {
  /**
   * Execute this function if Auth.currentAuthenticatedUser() returns a logged in user indicating there was no login redirect.
   */
  loggedInCallback?(): void;
  /**
   * Execute this callback if Auth.currentAuthenticatedUser should redirect. Executes asynchronously before redirect.
   */
  loginRedirectedCallback?(): void;
}

/**
 * Custom hook to get the healthy region and configure Amplify based on the available regions.
 *
 * @param loggedInCallback Callback function to invoke when user is logged in successfully.
 * @param loginRedirectedCallback Callback function to invoke when the user has failed to login.
 */
export const useLoadRegion = ({
  loggedInCallback,
  loginRedirectedCallback,
}: AmplifyInitProps): UseQueryResult => {
  const { state, dispatch } = useContext(AppContext);

  /**
   * Get the healthy region.
   */
  const loadRegion = async (): Promise<
    AxiosResponse<RegionHealthCheckResponseSchema>
  > => {
    const healthCheckUrl = `${
      getStageConfig()[DEFAULT_REGION].UNREGIONALIZED_API_ENDPOINT
    }/healthcheck`;
    // Call unregionalized endpoint since it should automatically route to an endpoint. It will return the region to use.
    // Call via axios.get since Cognito API interface isn't configured until after we know the region to use.
    return await axios.get(healthCheckUrl);
  };

  return useQuery("loadRegion", loadRegion, {
    onSuccess: (response: AxiosResponse<RegionHealthCheckResponseSchema>) => {
      const currentActiveRegion = state.activeRegion;
      const nextActiveRegion = response.data.region as ActivatedRegions;
      // Get regionalized configuration.
      const config = getAmplifyConfig(nextActiveRegion);

      logActiveRegionTransition(currentActiveRegion, nextActiveRegion);

      dispatch({
        type: ActionType.SET_API_ACTIVE_REGION,
        activeRegion: nextActiveRegion,
      });
      Amplify.configure(config);

      const { domain, redirectSignIn, responseType } = config.Auth.oauth;

      const clientId = config.Auth.userPoolWebClientId;

      // Omit the PPv2 login hint when `?auth=idprism` so Cognito routes to
      // IDPrism; otherwise (auth=ppv2, absent, or anything else) keep the
      // existing PPv2 redirect behavior.
      const authParam = new URLSearchParams(window.location.search).get(
        AUTH_PARAM
      );
      const loginHint = authParam === IDPRISM_AUTH_VALUE ? "" : PPV2_LOGIN_HINT;

      const cognitoLoginUrl =
        "https://" +
        domain +
        "/authorize?redirect_uri=" +
        redirectSignIn +
        "&response_type=" +
        responseType +
        "&client_id=" +
        clientId +
        loginHint;

      Auth.currentAuthenticatedUser()
        .then((user: CognitoUser) => {
          /**
           * Establish identity from the Cognito user as soon as authentication
           * resolves, before any backend API call. This guarantees the personId
           * is logged even if downstream calls (getUserAlias, getUserProgress)
           * fail with 403 or other errors. The useUserProgress onSuccess handler
           * will overwrite this with the canonical backend personId once the
           * API succeeds, which handles the edge case of new hires changing
           * their PPv2 email between Cognito sign-in and EZO Day 1 onboarding.
           */
          SnowplowTracker.setUserId({ user });

          // Only extract and pass token if electronAPI is available
          if (window.electronAPI?.setBearerToken) {
            try {
              // The ID token, the same one the EZO APIs take, so the agent can forward
              // it to CWB for server-side feature flags.
              const idToken = user
                .getSignInUserSession()
                ?.getIdToken()
                .getJwtToken();

              if (!idToken) {
                throw new Error("No ID token available in user session");
              }

              window.electronAPI.setBearerToken(idToken).catch((error) => {
                Logger.errorErrorLike("Failed to set bearer token", error);
              });
            } catch (tokenError) {
              Logger.errorErrorLike(
                "Failed to pass token to Electron",
                tokenError
              );
            }
          }

          trackStructEvent({
            category: Day1EventCategory.PageLevel,
            action: Day1EventAction.WizardLoad,
          });
          dispatch({
            type: ActionType.SET_AMPLIFY_CONFIGURED,
            amplifyConfigured: true,
          });
          loggedInCallback?.();
          return;
        })
        .catch((err: unknown) => {
          Logger.errorErrorLike(
            "Authentication error - failed to retrieve current authenticated user",
            err
          );
          loginRedirectedCallback?.();
          window.location.href = cognitoLoginUrl;
        });
    },
    onError: (errorLike: unknown) => {
      Logger.publishFatalMetric(
        new EmbeddedMetric("HealthcheckFailure", "Count", 1),
        "Service error - failed to initialize API active region",
        errorLike
      );
    },
    retry: (failureCount, err) => shouldRetry(failureCount, err),
    retryDelay: (retryCount) => getRetryDelayMs(retryCount),
  });
};
